/**
 * Single source of truth for "what is currently on screen that the guided hand-off has
 * to account for".
 *
 * Two guards in scanpack_item.js used to hand-maintain their own copy of this list, and
 * they had already drifted: `errorMessageClickScanConfirmation` was only in the card
 * guard, while `responseShowAction`, `reRenderFocus` and `boxChampOptionsBoxId` were
 * only in the focus guard. Both guards now derive their membership from the arrays
 * below, so a fifteenth modal is registered in one place and cannot be added to one
 * guard while being silently forgotten by the other.
 *
 * The two predicates are deliberately NOT the same and must not be collapsed into one:
 *
 *  - modalSuppressesHandoffCard() answers a DISPLAY question — "should the hand-off card
 *    keep out of the way?" It uses loose truthiness, matching the original
 *    boxChampModalOpen().
 *
 *    It must never be used to decide whether the box scan gate ENFORCES. Enforcement
 *    reads the modal-ignoring state via getBoxChampHandoff({ignoreModal: true}); routing
 *    the scan gate through this predicate is exactly the defect this module was split
 *    out to make hard to reintroduce.
 *
 *  - focusBlockingModalOpen() answers a FOCUS question — "would pulling focus into the
 *    barcode field steal it from something?" It uses strict `!== false` / `!== null`,
 *    matching the original setFocus chain, so a flag that is undefined (never
 *    initialised, or a prop the parent did not pass) counts as blocking rather than as
 *    closed. Yanking focus out from under a half-mounted modal is the failure that
 *    strictness guards against, so the looser test is not a safe substitute here.
 *
 * The asymmetries below are intentional, not leftovers:
 *
 *  - errorMessageClickScanConfirmation suppresses the card but does not block focus,
 *    because scanpack_item.js explicitly focuses `myInput` for that prompt right after
 *    the setFocus chain — it directs focus rather than withholding it.
 *  - responseShowAction / reRenderFocus / boxChampOptionsBoxId block focus but do not
 *    suppress the card: they are focus transitions and an inline sheet, none of which
 *    sit over the hand-off card.
 *
 * Changing any list here changes packer-visible behaviour, so
 * __tests__/boxChampModalFlags.spec.js pins it three ways:
 *
 *  - every array is asserted against a literal expected list, so an added, removed or
 *    renamed flag is a deliberate reviewed edit and never a silent one. Tests that merely
 *    iterate these arrays cannot catch a deletion — they just run one fewer case.
 *  - both predicates are compared against hand-written verbatim copies of the two
 *    original guards as they stood at 1dbc7d51, over single-flag, missing-key and 5000
 *    seeded combined states. That is what fails if a flag stops being honoured.
 *  - each asymmetry above is asserted explicitly, in both directions.
 */

// Flags read from ScanPackItem state that BOTH guards honour.
export const SHARED_MODAL_STATE_FLAGS = Object.freeze([
  'serialRecordPopUpShow',
  'typeScanAlert',
  'popUpModelShow',
  'clickScanConfirmation',
  'productConfirmationCheck',
  'orderConfirmPopUp',
  'addItemToOrder',
  'openRestartPrompt',
  'serviceIssuePrompt',
  'alert',
  'addBarcodeWindow',
  'boxChampDimsModalOpen',
]);

// Flags read from props that BOTH guards honour.
export const SHARED_MODAL_PROP_FLAGS = Object.freeze(['isBugOrSupportPopup']);

// Suppresses the hand-off card only. See the note on errorMessageClickScanConfirmation above.
export const CARD_SUPPRESSING_ONLY_STATE_FLAGS = Object.freeze([
  'errorMessageClickScanConfirmation',
]);

// Blocks focus only, boolean-valued.
export const FOCUS_BLOCKING_ONLY_STATE_FLAGS = Object.freeze([
  'responseShowAction',
  'reRenderFocus',
]);

// Blocks focus only, and holds an id rather than a boolean — "closed" is null, not false.
export const FOCUS_BLOCKING_ONLY_ID_STATE_FLAGS = Object.freeze([
  'boxChampOptionsBoxId',
]);

/**
 * DISPLAY predicate. True when something is on screen that the hand-off card should stay
 * out of the way of. Loose truthiness, mirroring the original boxChampModalOpen().
 */
export function modalSuppressesHandoffCard(state = {}, props = {}) {
  return Boolean(
    SHARED_MODAL_STATE_FLAGS.some(flag => state[flag]) ||
      CARD_SUPPRESSING_ONLY_STATE_FLAGS.some(flag => state[flag]) ||
      SHARED_MODAL_PROP_FLAGS.some(flag => props[flag]),
  );
}

/**
 * FOCUS predicate. True when focus must not be pulled into the barcode field. Strict
 * comparisons, mirroring the original setFocus chain: anything that is not explicitly
 * `false` (or `null`, for id-valued flags) counts as open.
 */
export function focusBlockingModalOpen(state = {}, props = {}) {
  return Boolean(
    SHARED_MODAL_STATE_FLAGS.some(flag => state[flag] !== false) ||
      FOCUS_BLOCKING_ONLY_STATE_FLAGS.some(flag => state[flag] !== false) ||
      FOCUS_BLOCKING_ONLY_ID_STATE_FLAGS.some(flag => state[flag] !== null) ||
      SHARED_MODAL_PROP_FLAGS.some(flag => props[flag] !== false),
  );
}

export const V2_SHARED_MODAL_PATHS = Object.freeze([
  'scanPackScanning.prompt.isVisible',
  'scanPackScanning.postScanPipeline.active',
  'scanPackScanning.alias.addBarcodeWindow',
  'scanPackScanning.alias.aliasVisible',
  'scanPackScanning.alias.shareBarcodeShow',
  'scanPackScanning.multiBoxAlert',
  'scanPackScanning.boxChamp.dimsModalOpen',
]);

export const V2_CARD_SUPPRESSING_ONLY_PATHS = Object.freeze([
  'scanPackScanning.shipment.visible',
  'user.isBugOrSupportPopup',
]);

export const V2_FOCUS_BLOCKING_ONLY_ID_PATHS = Object.freeze([
  'scanPackScanning.boxChamp.optionsBoxId',
]);

const readPath = (root, path) =>
  path.split('.').reduce((node, key) => (node == null ? undefined : node[key]), root);

export function v2ModalSuppressesHandoffCard(rootState = {}) {
  return Boolean(
    V2_SHARED_MODAL_PATHS.some(path => readPath(rootState, path)) ||
      V2_CARD_SUPPRESSING_ONLY_PATHS.some(path => readPath(rootState, path)),
  );
}

export function v2FocusBlockingModalOpen(rootState = {}) {
  return Boolean(
    V2_SHARED_MODAL_PATHS.some(path => readPath(rootState, path)) ||
      V2_FOCUS_BLOCKING_ONLY_ID_PATHS.some(path => readPath(rootState, path) != null),
  );
}
